import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Columns3,
  FileUp,
  Filter,
  FlaskConical,
  LayoutGrid,
  List,
  Rows3,
  Search,
  Star,
} from "lucide-react";
import { Fragment, type ReactNode, useMemo, useRef, useState } from "react";
import { importRecipeFile, type RecipeSummary, useChange, useRecipes } from "../api";
import {
  Empty,
  ErrorNote,
  Menu,
  MenuDivider,
  MenuHeading,
  MenuItem,
  Toolbar,
  ToolbarButton,
  usePageTitle,
} from "../components/ui";
import { showDate } from "../format";
import { useLayout } from "../layout/LayoutProvider";
import {
  type Layout,
  RECIPE_COLUMNS,
  type RecipeColumnId,
  type RecipeSortKey,
  type RecipeView,
} from "../layout/layout";
import { useNotify } from "../notify";
import { RECIPE_TYPE_LABELS, showGallons, showGravity } from "../recipeFormat";
import { filterRecipes, groupByStyle, sortRecipes, styleNames } from "../recipeViews";
import { score, wordsOf } from "../search";
import { ColumnOptions } from "./InventoryMenus";
import { beer, showAbv, styleLabel, useStar } from "./Recipe";

type RecipesLayout = Layout["recipes"];
export function RecipesPage() {
  usePageTitle("Recipes");
  const { data: recipes = [], isLoading, error } = useRecipes();
  const [q, setQ] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  const notify = useNotify();
  // Viewed, sorted and filtered the way Inventory is, and kept per device (Tyler, 2026-10-01).
  const { layout, update } = useLayout();
  const rv = layout.recipes;
  const setRv = (patch: Partial<RecipesLayout>) =>
    update((l) => ({ ...l, recipes: { ...l.recipes, ...patch } }));
  const importing = useChange(importRecipeFile, {
    onSuccess: (added) => {
      const only = added.length === 1 ? added[0] : undefined;
      notify(
        only
          ? { title: `${only.name} imported`, link: { kind: "recipe", recipeId: only.id } }
          : `${added.length} recipes imported`,
      );
    },
  });

  const searched = useMemo(() => {
    const words = wordsOf(q);
    if (words.length === 0) return recipes;
    return recipes.filter(
      (r) => score(words, r.name, [r.brewer, r.style?.name, ...r.ingredients]) > 0,
    );
  }, [recipes, q]);
  const shown = sortRecipes(filterRecipes(searched, rv), rv.sort);
  // Starred recipes in their own section at the top, which can be folded away (Tyler, 2026-10-01).
  const favorites = shown.filter((r) => r.favorite);
  const others = shown.filter((r) => !r.favorite);
  const filtered = rv.type !== null || rv.style !== null || rv.stock !== "all";

  return (
    <>
      <Toolbar
        left={
          <>
            {/* The builder lives here since Production took the sidebar (Tyler, 2026-10-03). */}
            <Link to="/recipes/builder" className="toolbar-button" title="Write a new recipe">
              <FlaskConical size={20} />
              <span className="label">Recipe builder</span>
            </Link>
            <ToolbarButton
              icon={<FileUp size={20} />}
              label="Import"
              title="Import recipes from BeerSmith (.bsmx) or BeerXML"
              disabled={importing.isPending}
              onClick={() => fileInput.current?.click()}
            />
          </>
        }
        right={
          <>
            <RecipeViewMenu view={rv.view} onChange={(view) => setRv({ view })} />
            <RecipeSortMenu sort={rv.sort} onChange={(sort) => setRv({ sort })} />
            <RecipeFilterMenu
              rv={rv}
              styles={styleNames(recipes)}
              active={filtered}
              onChange={setRv}
            />
            <RecipeOptionsMenu rv={rv} onChange={setRv} />
          </>
        }
      />
      <input
        ref={fileInput}
        type="file"
        accept=".bsmx,.xml,.beerxml"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) importing.mutate(file);
          e.target.value = "";
        }}
      />
      <div className="page-body">
        <h1 className="page-title">Recipes</h1>
        <ErrorNote error={error ?? importing.error} />
        {isLoading ? <p className="muted">Loading</p> : null}
        {!isLoading && recipes.length === 0 ? (
          // The same first-run note as an empty inventory, with Tyler's recipe art (2026-10-01).
          <div className="empty first-run">
            <img src="/recipes-256.png" alt="" />
            <h2>Your recipe book is empty</h2>
            <p>
              Import your recipes from BeerSmith or BeerXML, or write one in the recipe builder.
            </p>
            <div className="row" style={{ justifyContent: "center" }}>
              <button
                type="button"
                className="button primary"
                onClick={() => fileInput.current?.click()}
              >
                <FileUp size={16} /> Import recipes
              </button>
              <Link to="/recipes/builder" className="button">
                <FlaskConical size={16} /> Write a recipe
              </Link>
            </div>
          </div>
        ) : null}
        {recipes.length > 0 ? (
          <>
            <div className="recipe-search">
              <Search size={16} aria-hidden />
              <input
                type="search"
                className="input"
                placeholder="Find by name, style, brewer or ingredient"
                aria-label="Find a recipe"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            {shown.length === 0 && q.trim() && searched.length === 0 ? (
              <p className="muted">No recipe matches “{q.trim()}”.</p>
            ) : null}
            {shown.length === 0 && searched.length > 0 ? (
              <Empty title="Nothing matches these filters">
                <button
                  type="button"
                  className="button"
                  onClick={() => setRv({ type: null, style: null, stock: "all" })}
                >
                  Clear filters
                </button>
              </Empty>
            ) : null}
            {favorites.length ? (
              <section className="recipe-favorites">
                <button
                  type="button"
                  className="recipe-section-toggle"
                  aria-expanded={!rv.favoritesCollapsed}
                  onClick={() => setRv({ favoritesCollapsed: !rv.favoritesCollapsed })}
                >
                  {rv.favoritesCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                  <Star size={16} className="star-on" aria-hidden />
                  Favorites <span className="count">{favorites.length}</span>
                </button>
                {rv.favoritesCollapsed ? null : (
                  <RecipeResults
                    rv={rv}
                    recipes={favorites}
                    grouped={false}
                    onSort={(sort) => setRv({ sort })}
                  />
                )}
              </section>
            ) : null}
            {favorites.length && others.length ? (
              <h2 className="recipe-section-heading">Other recipes</h2>
            ) : null}
            {others.length ? (
              <RecipeResults
                rv={rv}
                recipes={others}
                grouped={rv.groupByStyle}
                onSort={(sort) => setRv({ sort })}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
}
/** Recipes in the view chosen, grouped by style if asked. */
function RecipeResults({
  rv,
  recipes,
  grouped,
  onSort,
}: {
  rv: RecipesLayout;
  recipes: RecipeSummary[];
  grouped: boolean;
  onSort: (sort: RecipesLayout["sort"]) => void;
}) {
  const groups = grouped ? groupByStyle(recipes) : [{ style: null, recipes }];
  if (rv.view === "table") {
    return <RecipeTable rv={rv} groups={groups} grouped={grouped} onSort={onSort} />;
  }
  return (
    <>
      {groups.map((g) => (
        <Fragment key={g.style ?? ""}>
          {grouped ? <StyleHeading style={g.style} count={g.recipes.length} /> : null}
          {rv.view === "list" ? (
            <div className="recipe-list">
              {g.recipes.map((r) => (
                <RecipeRow key={r.id} recipe={r} />
              ))}
            </div>
          ) : (
            <div className="card-grid size-large">
              {g.recipes.map((r) => (
                <RecipeCard key={r.id} recipe={r} />
              ))}
            </div>
          )}
        </Fragment>
      ))}
    </>
  );
}
/**
 * A recipe's star: on, it puts the recipe in Favorites at the top of the list. Inside a card or a row,
 * which are links, pressing it stars and goes nowhere.
 */
function FavoriteStar({
  recipe: r,
  className,
}: {
  recipe: { id: string; favorite: boolean };
  className?: string;
}) {
  const { on, pending, toggle } = useStar(r);
  return (
    <button
      type="button"
      className={`icon-button favorite-star ${on ? "on" : ""} ${className ?? ""}`}
      aria-pressed={on}
      aria-label={on ? "Take the star off this recipe" : "Star this recipe"}
      title={on ? "Take the star off" : "Star this recipe"}
      disabled={pending}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle();
      }}
    >
      <Star size={18} />
    </button>
  );
}
const StyleHeading = ({ style, count }: { style: string | null; count: number }) => (
  <h2 className="recipe-group">
    {style ?? "No style"} <span className="count">{count}</span>
  </h2>
);
const RECIPE_SORTS: { id: RecipeSortKey; label: string }[] = [
  { id: "name", label: "Name" },
  { id: "style", label: "Style" },
  { id: "lastBrewed", label: "Last brewed" },
  { id: "updated", label: "Last changed" },
  { id: "og", label: "Original gravity" },
  { id: "fg", label: "Final gravity" },
  { id: "abv", label: "ABV" },
  { id: "ibu", label: "Bitterness" },
  { id: "srm", label: "Color" },
  { id: "batch", label: "Batch size" },
];
/** Figures and dates read biggest or newest first the first time they are picked; names A to Z. */
const firstDir = (key: RecipeSortKey): "asc" | "desc" =>
  key === "name" || key === "style" ? "asc" : "desc";
function RecipeViewMenu({
  view,
  onChange,
}: {
  view: RecipeView;
  onChange: (v: RecipeView) => void;
}) {
  const views: { id: RecipeView; label: string; icon: ReactNode }[] = [
    { id: "cards", label: "Cards", icon: <LayoutGrid size={20} /> },
    { id: "list", label: "List", icon: <Rows3 size={20} /> },
    { id: "table", label: "Table", icon: <List size={20} /> },
  ];
  const current = views.find((v) => v.id === view) ?? views[0];
  return (
    <Menu icon={current?.icon ?? <LayoutGrid size={20} />} label="View">
      {(close) =>
        views.map((v) => (
          <MenuItem
            key={v.id}
            checked={v.id === view}
            onClick={() => {
              onChange(v.id);
              close();
            }}
          >
            {v.label}
          </MenuItem>
        ))
      }
    </Menu>
  );
}
function RecipeSortMenu({
  sort,
  onChange,
}: {
  sort: RecipesLayout["sort"];
  onChange: (s: RecipesLayout["sort"]) => void;
}) {
  return (
    <Menu icon={<ArrowUpDown size={20} />} label="Sort">
      {() => (
        <>
          <MenuHeading>Sort by</MenuHeading>
          {RECIPE_SORTS.map((k) => (
            <MenuItem
              key={k.id}
              checked={sort.key === k.id}
              onClick={() =>
                onChange({
                  key: k.id,
                  dir: sort.key === k.id ? (sort.dir === "asc" ? "desc" : "asc") : firstDir(k.id),
                })
              }
            >
              {k.label}
              {sort.key === k.id ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
            </MenuItem>
          ))}
        </>
      )}
    </Menu>
  );
}
function RecipeFilterMenu({
  rv,
  styles,
  active,
  onChange,
}: {
  rv: RecipesLayout;
  styles: string[];
  active: boolean;
  onChange: (patch: Partial<RecipesLayout>) => void;
}) {
  const stocks: { id: RecipesLayout["stock"]; label: string }[] = [
    { id: "all", label: "Any" },
    { id: "ready", label: "Can brew now" },
    { id: "short", label: "Short of something" },
    { id: "unlinked", label: "Not all linked to inventory" },
  ];
  return (
    <Menu icon={<Filter size={20} />} label="Filter" badge={active}>
      {() => (
        <>
          <MenuHeading>Stock</MenuHeading>
          {stocks.map((s) => (
            <MenuItem
              key={s.id}
              checked={rv.stock === s.id}
              onClick={() => onChange({ stock: s.id })}
            >
              {s.label}
            </MenuItem>
          ))}
          <MenuDivider />
          <MenuHeading>Type</MenuHeading>
          <MenuItem checked={rv.type === null} onClick={() => onChange({ type: null })}>
            All types
          </MenuItem>
          {Object.entries(RECIPE_TYPE_LABELS).map(([id, label]) => (
            <MenuItem key={id} checked={rv.type === id} onClick={() => onChange({ type: id })}>
              {label}
            </MenuItem>
          ))}
          {styles.length ? (
            <>
              <MenuDivider />
              <MenuHeading>Style</MenuHeading>
              <MenuItem checked={rv.style === null} onClick={() => onChange({ style: null })}>
                All styles
              </MenuItem>
              {styles.map((name) => (
                <MenuItem
                  key={name}
                  checked={rv.style === name}
                  onClick={() => onChange({ style: name })}
                >
                  {name}
                </MenuItem>
              ))}
            </>
          ) : null}
        </>
      )}
    </Menu>
  );
}
function RecipeOptionsMenu({
  rv,
  onChange,
}: {
  rv: RecipesLayout;
  onChange: (patch: Partial<RecipesLayout>) => void;
}) {
  return (
    <Menu icon={<Columns3 size={20} />} label="Options">
      {() => (
        <>
          <MenuItem
            checked={rv.groupByStyle}
            onClick={() => onChange({ groupByStyle: !rv.groupByStyle })}
          >
            Group by style
          </MenuItem>
          {rv.view === "table" ? (
            <ColumnOptions
              columns={rv.columns}
              defs={RECIPE_COLUMNS}
              onChange={(columns) => onChange({ columns })}
            />
          ) : null}
        </>
      )}
    </Menu>
  );
}
/**
 * Whether the brewery could brew it, as far as its linked lines say. A short line is short once the
 * planned brews take theirs, which may be months off, so the list warns and the recipe says when.
 */
function StockPill({ stock }: { stock: RecipeSummary["stock"] }) {
  if (stock.lines === 0) return null;
  if (stock.short > 0) {
    return (
      <span
        className="chip warn"
        title="Short once the brews already planned take theirs. Open the recipe to see when."
      >
        {stock.short} short
      </span>
    );
  }
  if (stock.linked === 0) {
    return (
      <span className="chip plain faint-chip" title="Link its ingredients to inventory to check">
        Not linked to inventory
      </span>
    );
  }
  if (stock.linked < stock.lines) {
    return <span className="chip warn">{stock.lines - stock.linked} not linked</span>;
  }
  return <span className="chip ok">Can brew now</span>;
}
function RecipeCard({ recipe: r }: { recipe: RecipeSummary }) {
  const e = r.estimates;
  return (
    <Link
      to="/recipes/$recipeId"
      params={{ recipeId: r.id }}
      className="recipe-card"
      style={beer(e.srm)}
    >
      <FavoriteStar recipe={r} className="card-star" />
      <div className="recipe-pills">
        {r.style ? (
          <span className="chip plain" title={styleLabel(r.style)}>
            {styleLabel(r.style)}
          </span>
        ) : null}
        <span className="chip plain">{showGallons(r.batchLiters)}</span>
        <span className="chip plain">{RECIPE_TYPE_LABELS[r.type] ?? r.type}</span>
        <span className="chip plain">v{r.version}</span>
      </div>
      <h3>{r.name}</h3>
      <p className="recipe-desc">
        {r.description ?? r.style?.category ?? (r.ingredients.slice(0, 4).join(", ") || " ")}
      </p>
      <div className="recipe-people">
        {r.brewer ? <span className="chip accent plain">{r.brewer}</span> : null}
        <StockPill stock={r.stock} />
      </div>
      <div className="recipe-stats">
        <RecipeFigures estimates={e} />
        <CardStat label="Last brewed" value={r.lastBrewedOn ? showDate(r.lastBrewedOn) : "Never"} />
      </div>
    </Link>
  );
}
/** A recipe's five estimates, as its card and its row show them. */
function RecipeFigures({ estimates: e }: { estimates: RecipeSummary["estimates"] }) {
  return (
    <>
      <CardStat label="OG" value={showGravity(e.og)} />
      <CardStat label="FG" value={showGravity(e.fg)} />
      <CardStat label="ABV" value={showAbv(e.abv)} />
      <CardStat label="IBU" value={e.ibu.toFixed(0)} />
      <CardStat
        label="SRM"
        value={
          <>
            <span className="beer-swatch" aria-hidden />
            {e.srm.toFixed(1)}
          </>
        }
      />
    </>
  );
}
function CardStat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="recipe-stat">
      <span className="label">{label}</span>
      <span className="value num">{value}</span>
    </div>
  );
}
/** A recipe in the list: its beer's color down the left edge, as the cards carry it along the top. */
function RecipeRow({ recipe: r }: { recipe: RecipeSummary }) {
  const e = r.estimates;
  const facts = [
    r.style ? styleLabel(r.style) : null,
    RECIPE_TYPE_LABELS[r.type] ?? r.type,
    showGallons(r.batchLiters),
    `v${r.version}`,
    r.brewer,
  ].filter(Boolean);
  return (
    <Link
      to="/recipes/$recipeId"
      params={{ recipeId: r.id }}
      className="recipe-row"
      style={beer(e.srm)}
    >
      <span className="stripe" aria-hidden />
      <div className="recipe-row-main">
        <h3>{r.name}</h3>
        <div className="muted">{facts.join(" · ")}</div>
        <div className="recipe-row-pills">
          <StockPill stock={r.stock} />
          <span className="faint">
            {r.lastBrewedOn ? `Brewed ${showDate(r.lastBrewedOn)}` : "Never brewed"}
          </span>
        </div>
      </div>
      <FavoriteStar recipe={r} className="row-star" />
      <div className="recipe-row-figures">
        <RecipeFigures estimates={e} />
      </div>
    </Link>
  );
}
/** The table's columns that sort, and by what. */
const SORTS_BY_COLUMN: Partial<Record<RecipeColumnId, RecipeSortKey>> = {
  name: "name",
  style: "style",
  batch: "batch",
  og: "og",
  fg: "fg",
  abv: "abv",
  ibu: "ibu",
  srm: "srm",
  lastBrewed: "lastBrewed",
  updated: "updated",
};
const NUMERIC_COLUMNS = new Set<RecipeColumnId>([
  "batch",
  "og",
  "fg",
  "abv",
  "ibu",
  "srm",
  "version",
]);
function RecipeTable({
  rv,
  groups,
  grouped,
  onSort,
}: {
  rv: RecipesLayout;
  groups: { style: string | null; recipes: RecipeSummary[] }[];
  grouped: boolean;
  onSort: (sort: RecipesLayout["sort"]) => void;
}) {
  const navigate = useNavigate();
  const columns = rv.columns.filter((c) => c.visible);
  const label = (id: RecipeColumnId) => RECIPE_COLUMNS.find((c) => c.id === id)?.label ?? id;
  const cell: Record<RecipeColumnId, (r: RecipeSummary) => ReactNode> = {
    name: (r) => (
      <span className="name-with-star">
        <FavoriteStar recipe={r} />
        <Link
          to="/recipes/$recipeId"
          params={{ recipeId: r.id }}
          onClick={(e) => e.stopPropagation()}
        >
          {r.name}
        </Link>
      </span>
    ),
    style: (r) => r.style?.name ?? "",
    type: (r) => RECIPE_TYPE_LABELS[r.type] ?? r.type,
    batch: (r) => showGallons(r.batchLiters),
    og: (r) => showGravity(r.estimates.og),
    fg: (r) => showGravity(r.estimates.fg),
    abv: (r) => showAbv(r.estimates.abv),
    ibu: (r) => r.estimates.ibu.toFixed(0),
    srm: (r) => (
      <>
        <span className="beer-swatch" aria-hidden />
        {r.estimates.srm.toFixed(1)}
      </>
    ),
    stock: (r) => <StockPill stock={r.stock} />,
    brewer: (r) => r.brewer ?? "",
    version: (r) => r.version,
    lastBrewed: (r) => (r.lastBrewedOn ? showDate(r.lastBrewedOn) : "Never"),
    updated: (r) => showDate(r.updatedAt),
  };
  return (
    <div className="table-wrap">
      <table className="table recipe-table">
        <thead>
          <tr>
            {columns.map((c) => {
              const key = SORTS_BY_COLUMN[c.id];
              const active = key && rv.sort.key === key;
              return (
                <th key={c.id} className={NUMERIC_COLUMNS.has(c.id) ? "num" : undefined}>
                  {key ? (
                    <button
                      type="button"
                      onClick={() =>
                        onSort({
                          key,
                          dir: active ? (rv.sort.dir === "asc" ? "desc" : "asc") : firstDir(key),
                        })
                      }
                    >
                      {label(c.id)}
                      {active ? (
                        rv.sort.dir === "asc" ? (
                          <ArrowUp size={13} />
                        ) : (
                          <ArrowDown size={13} />
                        )
                      ) : null}
                    </button>
                  ) : (
                    label(c.id)
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.style ?? ""}>
              {grouped ? (
                <tr className="group-row">
                  <td colSpan={columns.length}>
                    {g.style ?? "No style"}
                    <span className="count">{g.recipes.length}</span>
                  </td>
                </tr>
              ) : null}
              {g.recipes.map((r) => (
                <tr
                  key={r.id}
                  className="clickable"
                  style={beer(r.estimates.srm)}
                  onClick={() => navigate({ to: "/recipes/$recipeId", params: { recipeId: r.id } })}
                >
                  {columns.map((c, i) => (
                    <td
                      key={c.id}
                      className={[
                        NUMERIC_COLUMNS.has(c.id) ? "num" : "",
                        c.id === "name" ? "name-cell" : "",
                        // The beer's color down the row's left edge, as in the list.
                        i === 0 ? "beer-edge" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {cell[c.id](r)}
                    </td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
